import React, { useEffect } from "react";
import { BiCopy } from "react-icons/bi";
import { useForm } from "react-hook-form";
import { BsCheckAll } from "react-icons/bs";
import { useParams } from "react-router-dom";

import Title from "./Title";
import Confirm from "./Confirm";
import IntiateForm from "./IntiateForm";
import { DeviationProps } from "./interface";
import Button from "../../components/atomic/Button";
import CopyText from "../../components/atomic/CopyText";

import DropdownWithSearch from "../../components/atomic/DropdownWithSearch";
import { LoanApplicationService } from "../../../unified-dashboard/src/services/LoanApplication";

import { PAGES_LIST } from "../../shared/utils/constants";
import { notify } from "../../shared/context/AlertContext";
import GetAuthContext from "../../shared/context/AuthContext";
import { getFormattedDate } from "../../shared/utils/DateUtil";
import { getFormattedCurrency } from "../../shared/utils/Global";

type ActionMap = Record<string, string> | null | undefined;
type Option = { name: string; value: string; style?: string };

const FIELDS = {
  annualInterest: {
    originalValue: "",
    proposedValue: "",
    validation: "ROI",
    label: "Interest Rate",
    isDisabled: true
  },
  processingFee: {
    originalValue: "",
    proposedValue: "",
    label: "Processing Fee",
    validation: "PROCESSINGFEE",
    isDisabled: true
  }
};

const L2_OPTIONS = [
  {
    name: "Approve",
    value: "accepted",
    style: "!bg-[#D1FADF] text-[#027A48] !border-[#027A48]"
  },
  {
    name: "Reject",
    value: "rejected",
    style: "!bg-[#FEE4E2] !border-[#B42318] !text-[#B42318]"
  }
];

const KEY_NAMES: Record<keyof typeof FIELDS, string> = {
  // amount: "loanAmount",
  // tenureMonths: "tenure",
  annualInterest: "interestRate",
  processingFee: "processingFee"
};

const convertActionToDropdown = (action: ActionMap): Option[] => {
  const entries: Option[] = Object.keys(action ?? {}).map((key) => ({
    value: key,
    name: (action as Record<string, string>)[key]
  }));

  return entries;
};

export default function Change(props: DeviationProps) {
  const loanOffer = props?.loanOfferDetails?.[0];

  const {
    watch,
    setValue,
    register,
    getValues,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm();

  const { fetchToken } = GetAuthContext();

  const formValues = watch();
  const { loanID } = useParams();
  const isSalesUserRef = React.useRef(false);

  const [workflowData, setWorkflowData] = React.useState<Option[] | null>(null);
  const [selectedAction, setSelectedAction] = React.useState<Option | null>(
    null
  );

  const isMFL_LAP = props?.isMFL_LAP || false;
  const isABFLBL = props?.isABFLBL;
  const isABFLPL = props?.isABFLPL;

  // props?.timelineInfo?.[0] contains tenure: {proposed, original, deviation} for BL
  const loanTermInfo = isABFLPL
    ? props?.loanDeviationInfo
    : props?.timelineInfo?.[0];

  const dynamicLoanTermInfo: Record<
    string,
    { previous?: string; proposed?: string } | undefined
  > =
    (loanTermInfo as unknown as Record<
      string,
      { previous?: string; proposed?: string } | undefined
    >) || {};

  Object.keys(FIELDS ?? {})?.forEach((key: string) => {
    if (isABFLBL) {
      (FIELDS as any)[key]["originalValue"] = loanOffer[key];
      (FIELDS as any)[key]["proposedValue"] =
        loanTermInfo?.[(KEY_NAMES as any)[key]]?.proposed;

      // Make the proposed fields non-editable based on the below check only for ABFL BL
      // if (pageConfig?.global?.isABFL && loanTermInfo?.isProposedFieldsEditable)
      //   FIELDS[key].isDisabled = true;
    }
  });

  (Object.keys(FIELDS) as Array<keyof typeof FIELDS>).forEach((key) => {
    if (
      loanTermInfo?.status?.toLowerCase() === "pending" &&
      loanTermInfo?.actionByGroupName === "Sales" &&
      isSalesUserRef.current &&
      !isMFL_LAP &&
      isABFLPL
    )
      (FIELDS as any)[key]["isDisabled"] = false;

    if (isABFLPL) {
      (FIELDS as any)[key]["originalValue"] =
        dynamicLoanTermInfo[(KEY_NAMES as any)[key]]?.previous ?? "";
      (FIELDS as any)[key]["proposedValue"] =
        dynamicLoanTermInfo[(KEY_NAMES as any)[key]]?.proposed ?? "";
    }
  });

  // Set form values after FIELDS is populated
  useEffect(() => {
    if (loanTermInfo?.tenure?.proposed && isABFLBL) {
      setValue("proposed.tenureMonths", loanTermInfo?.tenure?.proposed);
    }
  }, [loanTermInfo?.tenure?.proposed, setValue]);

  useEffect(() => {
    if (isABFLPL) {
      const getWorkflowAction = async () => {
        if (!loanTermInfo) return;

        try {
          const response = (await LoanApplicationService.getWorkflowReqData({
            token: fetchToken(),
            pageName: PAGES_LIST.LOAN_APPLICATION_LOAN_TERM_DEVIATION_TAB,
            currentStage: loanTermInfo?.currentStatus || "",
            taskId: loanTermInfo?.taskId || "",
            workflowInstanceId: loanTermInfo?.workflowInstanceId || "",
            isLenderPlatform: !props?.isLenderDashboard,
            sourceEntityId: props?.sourceEntityID || "",
            loanApplicationID: loanID as string
          })) as {
            status: boolean;
            data?: {
              action?: Record<string, string>;
              loggedInUserRbacGroup?: string;
            };
          };

          if (response?.status) {
            setWorkflowData(convertActionToDropdown(response?.data?.action));

            isSalesUserRef.current = [
              "Sales",
              "Sales B2B",
              "Business Admin- Sales",
              "Business Admin"
            ].includes(response?.data?.loggedInUserRbacGroup ?? "");
          } else {
            notify({
              title: "Error",
              type: "error",
              text: "There was some error in fetching the workflow data"
            });
          }
        } catch (error) {
          console.error("Error fetching workflow data:", error);
          notify({
            title: "Error",
            type: "error",
            text: "There was some error in fetching the workflow data"
          });
        }
      };

      getWorkflowAction();
    }
  }, [
    loanTermInfo,
    fetchToken,
    props?.isLenderDashboard,
    props?.sourceEntityID,
    loanID
  ]);

  // Set form values after FIELDS is populated
  useEffect(() => {
    if (loanTermInfo && (isABFLPL || isABFLBL)) {
      (Object.keys(FIELDS) as Array<keyof typeof FIELDS>).forEach((key) => {
        setValue(`original.${key as string}`, FIELDS[key].originalValue);
        setValue(`proposed.${key as string}`, FIELDS[key].proposedValue);
      });

      // Also set tenure fields value so they are part of form state being used in CalculateEMI
      setValue(
        "proposed.tenureMonths",
        loanOffer?.tenureMonths //(loanTermInfo as any)?.tenure?.proposed ?? "",
      );
    }
  }, [loanTermInfo, setValue]);

  const onSubmitHandler = async (data: Record<string, unknown>) => {
    let response;

    if (isABFLBL) {
      const payloadInfo = {
        ...loanTermInfo,
        loanApplicationID: loanID as string,
        loanOfferID: loanOffer?.loanOfferID,
        loanAmount: {
          previous: String(loanTermInfo?.loanAmount?.previous ?? ""),
          proposed: String(loanTermInfo?.loanAmount?.previous ?? ""),
          deviation: loanTermInfo?.loanAmount?.deviation
        },
        processingFee: {
          previous: String(data?.original?.processingFee ?? ""),
          proposed: String(data?.proposed?.processingFee ?? ""),
          deviation: loanTermInfo?.processingFee?.deviation
        },
        interestRate: {
          previous: String(data?.original?.annualInterest ?? ""),
          proposed: String(data?.proposed?.annualInterest ?? ""),
          deviation: loanTermInfo?.interestRate?.deviation
        },
        comments: data?.comments,
        requestAction: selectedAction?.value
      };

      const payload = {
        token: fetchToken(),
        payload: payloadInfo,
        loanApplicationID: loanID as string,
        pageName: PAGES_LIST.LOAN_APPLICATION_LOAN_TERM_DEVIATION_TAB
      };

      response = await LoanApplicationService.saveLoanTermDeviation(payload);
    } else if (isABFLPL) {
      const original = (data as any)?.original ?? {};
      const proposed = (data as any)?.proposed ?? {};

      const payloadInfo = {
        loanApplicationId: loanID as string,
        remarks: data?.comments || "",
        workflowInstanceId: loanTermInfo?.workflowInstanceId || "",
        event: "update_task",
        identifier_type: "LOAN_APPLICATION_ID",
        identifier_id: loanID as string,
        action: selectedAction?.value || "",
        additional_data: {
          id: loanTermInfo?.taskId || "",
          task: {
            metadata: {
              offerNegotiationParams: {
                previousInterestRate: Number(
                  original.annualInterest ??
                    loanTermInfo?.interestRate?.previous ??
                    0
                ),
                proposedInterestRate: Number(proposed.annualInterest ?? 0),
                previousProcessingFee: Number(
                  original.processingFee ??
                    loanTermInfo?.processingFee?.previous ??
                    0
                ),
                proposedProcessingFee: Number(proposed.processingFee ?? 0)
              }
            }
          }
        }
      };

      response = await LoanApplicationService.addWorkflowReqData({
        token: fetchToken(),
        pageName: PAGES_LIST.LOAN_APPLICATION_LOAN_TERM_DEVIATION_TAB,
        sourceEntityId: props?.sourceEntityID || "",
        loanApplicationID: loanID as string,
        payload: payloadInfo
      });
    }

    if (response?.status) {
      notify({
        type: "success",
        title: "Success",
        text: `Loan Term Deviation ${
          selectedAction?.value === "rejected" ? "Rejected" : "Approved"
        } Proposal Successfully`
      });

      setTimeout(() => window.location.reload(), 1000);
    } else {
      notify({
        title: "Error",
        type: "error",
        text: response.error || "Couldn't update Loan Term Deviation"
      });
    }
  };

  return (
    <div>
      <div className="sticky top-[-40px] bg-white">
        <Title
          title="Loan Term Deviation Proposal"
          close={() => props?.setIsSliderOpen(false)}
        />
      </div>

      {isABFLBL && (
        <>
          <div className="pb-6 border-b my-6">
            <div className="block text-xs font-medium text-gray-700 mb-2">
              Request ID
            </div>
            <CopyText
              styles={{
                container: "bg-[#FAFAFA]",
                input:
                  "text-gray-800 bg-transparent w-full border border border-gray-300 rounded-lg rounded-r-none border-r-0 py-3 h-auto",
                button:
                  "bg-[#FAFAFA] border border-l-0 border-gray-300 text-gray-50 h-auto w-auto px-3 py-2.5 h-[46px]"
              }}
              text={loanTermInfo?.requestID ?? ""}
              copyButton={<BiCopy className="h-5 w-5 text-[#777878]" />}
              copyingButton={<BsCheckAll className="h-5 w-5 text-[#027A48]" />}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="text-xs text-medium">
              <div>
                <span className="text-[#626262]">Updated by</span>
                <span className="text-[#777878] ml-1">
                  {loanTermInfo?.actionBy}
                </span>
              </div>
              <div className="">
                <span className="text-[#626262]">Updated on </span>
                <span className="text-[#777878] ml-1">
                  {getFormattedDate(loanTermInfo?.createdAt) ?? "-"}
                </span>
              </div>
            </div>

            <Button
              outline
              color="primary"
              onClick={() => {
                props?.setCurrentSlider("TIMELINE", true, props?.timelineInfo);
              }}
              className=" !bg-white border-none"
            >
              View Timeline
            </Button>
          </div>
        </>
      )}

      <div className="my-6 pb-6 border-b">
        <div className="text-sm text-[#626262] font-medium">
          General Comment
        </div>
        <div className="mt-1 text-xs font-normal text-[#777878]">
          {loanTermInfo?.comments || "-"}
        </div>
      </div>

      <form
        className="mt-5"
        onSubmit={handleSubmit(onSubmitHandler)}
      >
        <div>
          <div className="flex justify-between items-center border-b pb-5 mb-5">
            <div className="text-sm font-semibold text-[#626262]">
              Loan Amount
            </div>
            <div className="text-xl font-semibold">
              {/* {getFormattedCurrency(loanTermInfo?.loanAmount?.previous)} */}
              {getFormattedCurrency(loanOffer?.amount ?? 0)}
            </div>
          </div>

          <IntiateForm
            errors={errors}
            fields={FIELDS}
            isValid={isValid}
            register={register}
            loanOffer={loanOffer}
            getValues={getValues}
            textArea={{ label: "General Comments", name: "comments" }}
            isABFLBL={isABFLBL}
            isABFLPL={isABFLPL}
          />
        </div>

        <div className="mt-6 text-[#4C4C4D] text-xs font-normal">
          These actions will affect the entire proposal including all Loan terms
        </div>

        <div className="mt-2 flex justify-between items-center">
          <DropdownWithSearch
            multiple={false}
            hasSearch={false}
            mode="reverse"
            options={isABFLBL ? L2_OPTIONS : (workflowData ?? [])}
            emptyText="No action"
            styles={{
              container: `!w-56`,
              button: selectedAction?.style,
              dropdown: "shadow-[-2px_-2px_15px_-3px_rgba(0,0,0,0.5)]"
            }}
            onChange={(action: Option) => setSelectedAction(action)}
            selectedItems={selectedAction || { name: "", value: "" }}
            placeholder="Action on Proposal"
          />

          {isABFLBL ? (
            <Confirm action={selectedAction?.value ?? ""} />
          ) : (
            isABFLPL && (
              <Confirm
                action={selectedAction?.value ?? ""}
                hasOptions={(workflowData?.length ?? 0) > 0}
                disabled={(function () {
                  const { original = {}, proposed = {} } =
                    (formValues as any) || {};

                  const keys = ["annualInterest", "processingFee"] as const;

                  const allEqual = keys.every((key) => {
                    const orig = (original as any)?.[key];
                    const prop = (proposed as any)?.[key];

                    if (
                      prop === undefined ||
                      prop === "" ||
                      Number(orig) === Number(prop)
                    )
                      return true;
                  });

                  return allEqual;
                })()}
              />
            )
          )}
        </div>
      </form>
    </div>
  );
}
